import { fetchChallengeGroup } from '@actions/challengeGroups';
import { editChallengeScoreWeighting } from '@actions/challenges/editChallengeScoreWeighting';
import { RootState } from '@reducers/index';
import {
    getChallengeGroup,
    getChallengeGroupsFetchError,
    getChallengeGroupsIsFetching,
} from '@selectors/challengeGroups';
import {
    getChallengesIsPatching,
    getChallengesPatchError,
    getChallengesPatchSuccess,
} from '@selectors/challenges';
import useForm from '@components/form/hooks/useForm';
import usePrevious from 'lib/src/hooks/usePrevious';
import { clearFieldErrors } from 'lib/src/redux/actions/fieldErrors';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Challenge } from 'src/types/shared/Challenge';
import { ChallengeGroup } from 'src/types/shared/ChallengeGroup';

const useEditChallengeScoreWeighting = (challenge: Challenge, hideModal: () => void): Hook => {
    const dispatch = useDispatch();

    const challengeGroup = useSelector((state: RootState) =>
        getChallengeGroup(state, challenge.challengeGroupID),
    );
    const isFetching = useSelector(getChallengeGroupsIsFetching);
    const fetchError = useSelector(getChallengeGroupsFetchError);
    const isPatching = useSelector(getChallengesIsPatching);
    const patchError = useSelector(getChallengesPatchError);
    const patchSuccess = useSelector(getChallengesPatchSuccess);
    const prevProps = usePrevious({
        isFetching,
        patchSuccess,
        challenge,
    });

    const [formState, handleChange, setForm] = useForm({
        deploymentScale: challenge.scoreWeighting?.deploymentScale ?? 0.2,
        fitWithChallenge: challenge.scoreWeighting?.fitWithChallenge ?? 0.2,
        credibilityCompany: challenge.scoreWeighting?.credibilityCompany ?? 0.2,
        credibilityTechnology: challenge.scoreWeighting?.credibilityTechnology ?? 0.2,
        fullOrPartialSolution: challenge.scoreWeighting?.fullOrPartialSolution ?? 0.2,
    });

    useEffect(() => {
        if (challenge?.challengeGroupID) {
            dispatch(fetchChallengeGroup(challenge.challengeGroupID));
        }
    }, [dispatch, challenge.challengeGroupID]);

    // * handle weighting fetched after page load
    useEffect(() => {
        if (challenge.scoreWeighting && !prevProps.challenge.scoreWeighting) {
            setForm({ ...challenge.scoreWeighting });
        }
    }, [challenge, prevProps.challenge, setForm]);

    useEffect(() => {
        if (patchSuccess && !prevProps.patchSuccess) {
            hideModal();
        }
    }, [patchSuccess, prevProps, hideModal]);

    const handleSubmit = () => {
        dispatch(editChallengeScoreWeighting(challenge.id, formState));
    };
    const handleWeightingChange = (name: keyof FormState, value: number) => {
        dispatch(clearFieldErrors());
        const percentageValue = value * 100;
        const rounded = Math.round(percentageValue) / 100;
        handleChange(name, rounded);
    };
    return {
        challengeGroup,
        isFetching,
        fetchError,
        formState,
        handleChange: handleWeightingChange,
        handleSubmit,
        isPatching,
        patchError,
        patchSuccess,
        prevSuccess: prevProps.patchSuccess,
    };
};

interface FormState {
    deploymentScale: number;
    fitWithChallenge: number;
    credibilityCompany: number;
    credibilityTechnology: number;
    fullOrPartialSolution: number;
}

interface Hook {
    challengeGroup: ChallengeGroup;
    isFetching: boolean;
    fetchError: string | null;
    isPatching: boolean;
    patchError: string | null;
    patchSuccess: boolean;
    prevSuccess: boolean;
    formState: FormState;
    handleChange: (name: keyof FormState, value: number) => void;
    handleSubmit: () => void;
}

export default useEditChallengeScoreWeighting;
